import { useCallback, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type AuthError,
} from 'firebase/auth';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';

import { auth, db, googleProvider } from '@/lib/firebase';
import { useStore } from '@/app/store';
import { playerSchema, type Player } from '@/lib/schemas';

/* =====================================================================
   useGoogleAuth — sustituye useAnonymousAuth.

   - signIn(): popup de Google
   - signOutNow(): cierra sesión + limpia store
   - Auto-restaura el currentPlayer en /players cuando la sesión arranca,
     buscando por ownerUid === user.uid (cada Google user = 1 player).
===================================================================== */

export function useGoogleAuth() {
  const setUser = useStore((s) => s.setUser);
  const clearUser = useStore((s) => s.clearUser);
  const setAuthError = useStore((s) => s.setAuthError);
  const setCurrentPlayer = useStore((s) => s.setCurrentPlayer);
  const clearCurrentPlayer = useStore((s) => s.clearCurrentPlayer);

  // Detección global de cambio de sesión
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        clearUser();
        clearCurrentPlayer();
        return;
      }
      setUser({
        uid: u.uid,
        email: u.email,
        displayName: u.displayName,
        photoURL: u.photoURL,
      });
      // Auto-restaurar player si ya existe registro con este ownerUid
      await restorePlayerForUid(u.uid, setCurrentPlayer);
    });
    return unsub;
  }, [setUser, clearUser, setAuthError, setCurrentPlayer, clearCurrentPlayer]);

  const signIn = useCallback(async () => {
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      // Restore inline → cuando esto resuelva, el caller puede leer
      // store.currentPlayer y decidir si va a /menu o /register.
      await restorePlayerForUid(credential.user.uid, setCurrentPlayer);
    } catch (err) {
      const e = err as AuthError;
      console.error('[google-auth] signInWithPopup:', e.code, e.message);
      setAuthError(e.code ?? 'unknown');
      throw e;
    }
  }, [setAuthError, setCurrentPlayer]);

  const signOutNow = useCallback(async () => {
    await signOut(auth);
    // onAuthStateChanged → clearUser + clearCurrentPlayer
  }, []);

  return { signIn, signOutNow };
}

/* -------------------------------------------------------------------- */

async function restorePlayerForUid(
  uid: string,
  setCurrentPlayer: (id: string, p: Player) => void,
): Promise<void> {
  try {
    const q = query(ref(db, 'players'), orderByChild('ownerUid'), equalTo(uid));
    const snap = await get(q);
    if (!snap.exists()) return;
    const all = snap.val() as Record<string, unknown>;
    const [id, raw] = Object.entries(all)[0];
    const parsed = playerSchema.safeParse(raw);
    if (parsed.success) setCurrentPlayer(id, parsed.data);
  } catch (err) {
    console.warn('[google-auth] restorePlayer failed:', err);
  }
}
